import { BaseComplianceModels } from '@getgreenline/compliance'
import { ComplianceTimeFrame } from '@getgreenline/compliance/build/models/BaseComplianceModels'
import { dayjs } from '../../../../utilities/dayjs'
import { observable, action } from 'mobx'
import moment from 'moment'
import { browserHistory } from 'react-router'
import { monthType, TQuarterFirstMonth } from '../../../../components/Input/MonthSelector'
import { parseQueryParameterIntegerArray } from '../../../../utilities/parseQueryParameterIntegerArray'
import { CompaniesModels } from '@getgreenline/companies'

export enum ComplianceStep {
  LocationMonth = 1,
  Fix = 2,
  Preview = 3,
  Generate = 4,
}

interface Props {
  province: CompaniesModels.Province
}

export class ComplianceStore {
  @observable
  currentStep: ComplianceStep = ComplianceStep.LocationMonth

  @observable
  month: monthType = moment().subtract(1, 'month').month() as monthType // this will return 0 - 11 => selecting the previous month as default

  @observable
  year: number = moment().subtract(1, 'month').year() // selecting the previous month's year as default

  @observable
  locationIds: number[] = []

  @observable
  timeFrame: BaseComplianceModels.ComplianceTimeFrame =
    BaseComplianceModels.ComplianceTimeFrame.MONTHLY

  constructor(props: Props) {
    const {
      month,
      year,
      locationIds,
      currentStep: currentStepFromQuery,
      timeFrame,
    } = browserHistory.getCurrentLocation().query
    const parsedMonth = parseInt(month, 10)
    const parsedYear = parseInt(year, 10)
    const parsedCurrentStep = parseInt(currentStepFromQuery, 10)

    const isMonthValid = !!parsedMonth && month <= 12 && month >= 1
    if (isMonthValid) {
      this.month = parsedMonth as monthType
    }
    if (parsedYear) {
      this.year = year
    }
    if (!!parsedCurrentStep && Object.keys(ComplianceStep).includes(currentStepFromQuery)) {
      this.currentStep = parsedCurrentStep
    }
    if (locationIds) {
      if (Array.isArray(locationIds)) {
        this.locationIds = parseQueryParameterIntegerArray(locationIds)
      } else {
        const parsedLocationId = parseInt(locationIds, 10)
        if (parsedLocationId) {
          this.locationIds = [parsedLocationId]
        }
      }
    }

    if (props.province === CompaniesModels.Province.MB) {
      if (timeFrame === BaseComplianceModels.ComplianceTimeFrame.QUARTERLY) {
        this.timeFrame = timeFrame
        this.changeMonth(this.getFirstMonthOfQuarter(this.month))
      }
    }

    this.updateUrl()
  }

  // This will be used to listen to url changes and override url
  browserHistoryListener: () => void = () => {}

  @action
  observeCurrentStepInUrl() {
    this.browserHistoryListener = browserHistory.listen((location) => {
      const parsedCurrentStep = parseInt(location.query.currentStep, 10)
      if (!!parsedCurrentStep && parsedCurrentStep !== this.currentStep) {
        this.currentStep = parsedCurrentStep
      }
    })
  }

  unObserveCurrentStepInUrl() {
    this.browserHistoryListener()
  }

  getFirstMonthOfQuarter = (month: monthType) => {
    return (month - ((month - 1) % 3)) as TQuarterFirstMonth
  }

  generateEndDate = (startDate: string, timeFrame: ComplianceTimeFrame) => {
    return timeFrame === ComplianceTimeFrame.QUARTERLY
      ? dayjs(startDate).add(2, 'M').endOf('month').format('YYYY-MM-DD')
      : dayjs(startDate).endOf('month').format('YYYY-MM-DD')
  }

  generateFileName = (companyName: string, extensionType: string) => {
    const formattedMonth = dayjs()
      .month(this.month - 1)
      .format('MM')

    const formattedYear = this.year.toString().substr(2)

    if (this.timeFrame === ComplianceTimeFrame.QUARTERLY) {
      const lastMonthOfQuarter = dayjs()
        .month(this.month - 1)
        .add(2, 'M')
        .format('MM')

      return `${companyName}_compliance-report_${formattedMonth}${formattedYear}${`_${lastMonthOfQuarter}${formattedYear}`}.${extensionType}`
    }

    return `${companyName}_compliance-report_${formattedMonth}${formattedYear}.${extensionType}`
  }

  @action
  changeMonth(newMonth: monthType) {
    this.month = newMonth
  }

  @action
  changeYear(newYear: number) {
    this.year = newYear
  }

  @action
  toggleLocationId(locationId: number) {
    if (this.locationIds.includes(locationId)) {
      this.locationIds = this.locationIds.filter((lId) => lId !== locationId)
    } else {
      this.locationIds = [...this.locationIds, locationId]
    }
  }

  @action
  changeTimeFrame = (newTimeFrame: BaseComplianceModels.ComplianceTimeFrame) => {
    this.timeFrame = newTimeFrame
  }

  @action
  fromCurrentStepToNextStep = () => {
    let nextStep: ComplianceStep = this.currentStep
    switch (this.currentStep) {
      case ComplianceStep.LocationMonth:
        nextStep = ComplianceStep.Fix
        break
      case ComplianceStep.Fix:
        nextStep = ComplianceStep.Preview
        break
      case ComplianceStep.Preview:
        nextStep = ComplianceStep.Generate
        break
      default:
        break
    }

    this.currentStep = nextStep
    this.updateUrl()
  }

  @action
  toSpecificStep(nextStep: ComplianceStep) {
    if (!this.month || !this.year || this.locationIds.length === 0) {
      return
    }

    this.currentStep = nextStep
    this.updateUrl()
  }

  updateUrl() {
    browserHistory.push({
      pathname: browserHistory.getCurrentLocation().pathname,
      query: {
        month: this.month,
        year: this.year,
        currentStep: this.currentStep,
        locationIds: this.locationIds,
        timeFrame: this.timeFrame,
      },
    })
  }
}
